import Panel from '@/components/panel/Panel.jsx';
import { LuContainer, MdWebhook } from '@/components/react-icons/main';
import { DeleteIcon } from '@/lib/constants/icons';
import { runStepHook } from '@/redux/actions/gravityActions.js';
import { setModalState } from '@/redux/actions/uiActions/modalActions';
import waitForSyncReady from '@/redux/actions/waitForSyncReady';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';
import selectors from '@/redux/selectors/selectors';
import CredentialStatusIcon from '@/screens/credentials/components/CredentialStatusIcon';
import stepActions from '@/screens/workflows/redux/actions/stepActions.js';
import workflowScreenSelectors from '@/screens/workflows/redux/selectors/workflowScreenSelectors.js';
import openTutorialAuthWindow from '@/screens/workflows/workflowEditor/workflowEditorPanels/primaryPanels/appPanel/appCredentialsPane/openTutorialAuthWindow';
import { appSchema } from '@shared/schema/appSchema.js';
import { useState } from 'react';
import { useSelector } from 'react-redux';

const CredentialListItem = ({ credential, step, appTriggerLabel, isActive, onDelete, hideDelete, disabled = false }) => {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const canEditCredentials = useSelector(selectors.canManageCompanyCredentials);
  const workflowIsEditable = useSelector(workflowScreenSelectors.activeWorkflowIsEditable);
  const tutorialSlug = useSelector((state) => workflowScreenSelectors.activeWorkflowMetadata(state)?.tutorialSlug);
  const [onAuthSuccessLoading, setOnAuthSuccessLoading] = useState(false);
  const isLoadingM2MOauthToken = useArgSelector(selectors.isLoading, `getM2MOauthToken-${credential?.value}`);
  const stepCredentialColorMap = useSelector(workflowScreenSelectors.activeWorkflowCredentialColors);
  const usesEnvironments = useSelector(workflowScreenSelectors.activeWorkflowUsesEnvironments);
  const environments = useSelector(selectors.environments);
  const stepCredentialColor = stepCredentialColorMap?.[credential?.value];
  const isDisabled = disabled || !canEditCredentials || !workflowIsEditable;
  const isUsedInProduction = credential?.usage > 0;
  const tags = isUsedInProduction //
    ? [{ label: `Used in ${credential.usage} active workflow${credential.usage === 1 ? '' : 's'}`, props: { variant: credential.usage > 0 ? 'blue' : '', size: 'sm' } }]
    : [];

  const authType = appSchema.authentication.types.find((type) => type.type === credential?.type)?.label;
  const environmentId = credential?.environmentId;

  const variant = (environmentId) => {
    const name = environments?.find((e) => e.id === environmentId)?.name;
    if (name === 'Sandbox') {
      return 'warning';
    }
    if (name === 'Production') {
      return 'accent';
    }
    return 'info';
  };

  const handleOnAuthSuccess = async () => {
    setOnAuthSuccessLoading(true);
    await waitForSyncReady();
    runStepHook({ workflowId: step?.workflowId, stepId: step?.id, actionId: step?.actionId, hook: 'onAuthSuccess', callback: () => setOnAuthSuccessLoading(false) });
  };

  const handleEdit = (e) => {
    e?.stopPropagation();
    if (tutorialSlug) {
      openTutorialAuthWindow();
      return;
    }
    setModalState('credential', { open: true, credentialId: credential.value });
  };

  const handleSetActive = (e) => {
    e?.stopPropagation();
    stepActions.set('credentialId', credential.value);
  };

  const handleDelete = (e) => {
    e?.stopPropagation();
    if (!deleteConfirmOpen) {
      setDeleteConfirmOpen(true);
      return;
    }
    if (isUsedInProduction) {
      setModalState('cannotDeleteCredential', { open: true, credentialId: credential?.value });
      return;
    }
    onDelete(credential);
    setDeleteConfirmOpen(false);
  };

  const primaryAction = isActive ? { label: 'Edit', onClick: handleEdit } : { label: 'Set Active', onClick: handleSetActive };
  const actions = [
    { icon: MdWebhook, title: appTriggerLabel, onClick: handleOnAuthSuccess, loading: onAuthSuccessLoading || isLoadingM2MOauthToken, visible: !!appTriggerLabel && credential?.status === 'complete' },
    { icon: !deleteConfirmOpen && DeleteIcon, label: deleteConfirmOpen && 'Are you sure?', className: `delete-btn hidden ${deleteConfirmOpen ? 'open' : ''}`, title: 'Delete', onClick: handleDelete, visible: !hideDelete, props: { onMouseLeave: () => setDeleteConfirmOpen(false) } }
  ].filter((action) => action.visible);

  const environmentTag = {
    label: environments.find((e) => e.id === environmentId)?.name,
    props: {
      variant: variant(environmentId),
      icon: <LuContainer />,
      iconVariant: ''
    }
  };

  const titleTags = [...(authType ? [{ label: authType }] : [])];
  if (environmentTag?.label && usesEnvironments) {
    titleTags.push(environmentTag);
  }

  return (
    <Panel.FeatureBox //
      disabled={isDisabled}
      configured={isActive}
      className={isActive ? 'active' : ''}
      name={credential?.label}
      optionType={'credential'}
      icon={<CredentialStatusIcon credential={credential} />}
      description={`Updated at ${credential?.lastUpdated}`}
      titleTags={titleTags}
      actions={actions}
      tags={tags}
      onClick={primaryAction.onClick}
      pointerText={primaryAction.label}
      colorTag={stepCredentialColor}
      tagDataTitle="All steps with this color are using this connection."
    />
  );
};

export default CredentialListItem;
